// api.js — BSC Gate Automation API client v6 (manual forms + live stats)

import axios from "axios";

const BASE = process.env.REACT_APP_API_BASE || "https://api.bhagwatistonecrushers.com/api/v1/gate";

const http = axios.create({ baseURL: BASE });

http.interceptors.request.use(config => {
  const token = localStorage.getItem("bsc_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

http.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      // session is no longer valid — flag it so the login page can explain why
      localStorage.removeItem("bsc_token");
      localStorage.removeItem("bsc_user");
      sessionStorage.setItem("bsc_session_expired", "1");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Fetches a PDF as a blob and triggers a browser download for it.
// Reads the real filename off Content-Disposition when the backend exposes it
// over CORS; falls back to fallbackName otherwise.
async function downloadPdf(path, params, fallbackName) {
  const cleanParams = {};
  if (params?.from) cleanParams.from = params.from;
  if (params?.to) cleanParams.to = params.to;

  let res;
  try {
    res = await http.get(path, { params: cleanParams, responseType: "blob" });
  } catch (err) {
    throw new Error("Could not generate PDF. Please try again.");
  }

  const disposition = res.headers?.["content-disposition"];
  const match = disposition && /filename="?([^"; ]+)"?/i.exec(disposition);
  const filename = match ? match[1] : fallbackName;

  const url = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const gateApi = {

  // ── Auth ───────────────────────────────────────────────────────────────────
  // NOTE: the dashboard logs in via AuthContext.login (which sets user state).
  // This helper is kept for non-React callers only.
  login: async (username, password) => {
    const res = await http.post("/auth/login", { username, password });
    if (res.data.token) localStorage.setItem("bsc_token", res.data.token);
    return res.data;
  },

  logout: () => {
    localStorage.removeItem("bsc_token");
    localStorage.removeItem("bsc_user");
    window.location.href = "/login";
  },

  // ── Live stakeholder KPIs ────────────────────────────────────────────────────
  getLiveStats: async () => {
    const res = await http.get("/stats/live");
    return res.data;
  },

  // ── Entry form (manual, no ANPR) ────────────────────────────────────────────
  directEntry: async ({ licensePlate, vehicleType, expectedLoad, operatorNotes }) => {
    const res = await http.post("/entry/direct", {
      licensePlate, vehicleType, expectedLoad, operatorNotes,
    });
    return res.data;
  },

  // ── Exit form ────────────────────────────────────────────────────────────────
  getActiveVehicles: async () => {
    const res = await http.get("/exit/active-vehicles");
    return res.data;
  },

  getLatestExitPhoto: async () => {
    const res = await http.get("/exit/latest-photo");
    return res.data;
  },

  // ── Exit camera (operator-triggered, relayed to the Pi by the backend) ───────
  // Takes the photo NOW and returns { captureId, s3Key, sizeKb, thumbnail }.
  // The image stays on the Pi until the exit form is submitted, so nothing is
  // uploaded to S3 for a capture the operator retakes or abandons.
  captureExitPhoto: async () => {
    const res = await http.post("/exit/camera/capture");
    return res.data;
  },

  // Operator retook the photo — drop the previous file on the Pi.
  discardExitPhoto: async (captureId) => {
    const res = await http.delete("/exit/camera/" + captureId);
    return res.data;
  },

  completeExit: async (txId, { photoId, captureId, s3Key, freightClassification, operatorNotes }) => {
    const res = await http.patch("/exit/complete/" + txId, {
      photoId,
      captureId,
      s3Key,
      freightClassification,
      operatorNotes,
    });
    return res.data;
  },

  // ── Edit a transaction (audited, reason required) ────────────────────────────
  updateTransaction: async (txId, { licensePlate, vehicleType, expectedLoad, freightClassification, operatorNotes, reasonCode, reasonText }) => {
    const res = await http.patch("/transaction/" + txId, {
      licensePlate, vehicleType, expectedLoad, freightClassification, operatorNotes,
      reasonCode, reasonText,
    });
    return res.data;
  },

  // ── Reject a vehicle (admin only, audited) ────────────────────────────────────
  rejectTransaction: async (txId, { reasonCode, reasonText }) => {
    const res = await http.patch("/transaction/" + txId + "/reject", { reasonCode, reasonText });
    return res.data;
  },

  getTransactionHistory: async (txId) => {
    const res = await http.get("/transaction/" + txId + "/history");
    return res.data;
  },

  // ── Admin: flagged plate changes awaiting review ─────────────────────────────
  getFlaggedEdits: async () => {
    const res = await http.get("/transactions/flagged");
    return res.data;
  },

  reviewEdit: async (editId) => {
    const res = await http.patch("/transaction/edit/" + editId + "/review", {});
    return res.data;
  },

  // ── Transactions (dashboard list, paginated) ─────────────────────────────────
  // Returns { items, page, size, totalElements, totalPages, hasMore }.
  getTransactions: async ({ page = 0, size = 50, status, search } = {}) => {
    const params = { page, size };
    if (status) params.status = status;
    if (search) params.search = search;
    const res = await http.get("/transactions", { params });
    return res.data;
  },

  // ── Overstays (vehicles inside > 24h) ────────────────────────────────────────
  getOverstays: async () => {
    const res = await http.get("/overstays");
    return res.data;
  },

  // ── Reports / analytics ──────────────────────────────────────────────────────
  getMaterialTrips: async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await http.get("/reports/material-trips", { params });
    return res.data;
  },

  getVehicleLoads: async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await http.get("/reports/vehicle-loads", { params });
    return res.data;
  },

  downloadMaterialTripsPdf: ({ from, to } = {}) =>
    downloadPdf("/reports/material-trips/pdf", { from, to }, "material-trips.pdf"),

  downloadVehicleLoadsPdf: ({ from, to } = {}) =>
    downloadPdf("/reports/vehicle-loads/pdf", { from, to }, "vehicle-loads.pdf"),

  // ── Photo review (PHOTO_REVIEWER role only) ──────────────────────────────────
  getPhotos: async ({ from, to, search, page = 0, size = 24 } = {}) => {
    const params = { page, size };
    if (from) params.from = from;
    if (to) params.to = to;
    if (search) params.search = search;
    const res = await http.get("/photos", { params });
    return res.data;
  },

  // ── Weighbridge (WEIGHBRIDGE_OPERATOR role) — tare then gross, two visits ────
  getWeighbridgePending: async () => {
    const res = await http.get("/weighbridge/pending");
    return res.data;
  },

  recordTareWeight: async (txId, tareWeightTonnes) => {
    const res = await http.patch("/weighbridge/tare/" + txId, { tareWeightTonnes });
    return res.data;
  },

  recordGrossWeight: async (txId, grossWeightTonnes) => {
    const res = await http.patch("/weighbridge/gross/" + txId, { grossWeightTonnes });
    return res.data;
  },

  // Wider net than getWeighbridgePending() — vehicles that already have at
  // least one weight recorded (not just ones waiting on their next step).
  getWeighbridgeCorrectable: async () => {
    const res = await http.get("/weighbridge/correctable");
    return res.data;
  },

  correctTareWeight: async (txId, tareWeightTonnes, reasonCode, reasonText) => {
    const res = await http.patch("/weighbridge/correct-tare/" + txId, { tareWeightTonnes, reasonCode, reasonText });
    return res.data;
  },

  correctGrossWeight: async (txId, grossWeightTonnes, reasonCode, reasonText) => {
    const res = await http.patch("/weighbridge/correct-gross/" + txId, { grossWeightTonnes, reasonCode, reasonText });
    return res.data;
  },

  // ── Challan (CHALAN_OPERATOR role) ────────────────────────────────────────────
  getChallanPending: async () => {
    const res = await http.get("/challan/pending");
    return res.data;
  },

  generateChallan: async (txId) => {
    const res = await http.post("/challan/generate/" + txId);
    return res.data;
  },

  // Keyed by transaction, not challan — payment is often collected before the
  // government challan is issued, so there may be no challan to address yet.
  // amountCollected is optional — omit to charge the calculated figure, pass a
  // number when the customer is on an agreed rate. Any override is audited.
  markChallanPaid: async (txId, amountCollected) => {
    const body = amountCollected == null ? {} : { amountCollected };
    const res = await http.patch("/challan/payment-received/" + txId, body);
    return res.data;
  },

  // ── Material prices (₹/tonne, admin-configurable) ─────────────────────────────
  getMaterialPrices: async () => {
    const res = await http.get("/material-prices");
    return res.data;
  },

  setMaterialPrice: async (material, pricePerTonne) => {
    const res = await http.put("/material-prices/" + encodeURIComponent(material), { pricePerTonne });
    return res.data;
  },

  // ── Reject vehicle: eligible vehicles (admin only) ────────────────────────────
  getRejectableVehicles: async () => {
    const res = await http.get("/transactions/rejectable");
    return res.data;
  },

  // Every vehicle still somewhere in the yard chain — backs UpdateForm.jsx.
  // Unlike getActiveVehicles() (PENDING_EXIT only), this covers every stage.
  getInProgressVehicles: async () => {
    const res = await http.get("/transactions/in-progress");
    return res.data;
  },

  // ── Charity dispatches ─────────────────────────────────────────────────────
  // Stone given away. A flow of its own: no weighbridge, no challan, no
  // payment, and nothing here reaches the sales or tonnage reports.
  recordCharityDispatch: async ({ licensePlate, material, loadingFrom, destination, exitTime }) => {
    const res = await http.post("/charity", {
      licensePlate, material, loadingFrom, destination, exitTime,
    });
    return res.data;
  },

  getCharityDispatches: async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await http.get("/charity", { params });
    return res.data;
  },
};

export default gateApi;